import { AlertTriangle, Cpu, Hand, Loader2, Plus } from "lucide-react";
import { useState, useEffect, useMemo } from "react";

import AlertDialog from "@/common/components/popups/AlertDialog";
import DialogBox from "@/common/components/popups/DialogBox";
import TextLabel from "@/common/components/TextLabel";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";
import { useCreateCycle } from "@/common/services/cycles";
import { useMobileApps } from "@/common/services/mobile-apps/mobile-apps.queries";
import { useCreateConfiguration } from "@/common/services/cycle-configurations";
import { useReleaseFolders } from "@/common/services/release-folders";
import type { Cycle, CycleType } from "@/common/types/Cycle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CreateCycleDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (cycle: Cycle) => void;
  defaultType?: CycleType;
  defaultFolderId?: number | null;
  versionId?: number;
  projectId?: number;
  selectedTestCaseType?: "AUTOMATION" | "MANUAL";
}

export function CreateCycleDialog({
  open,
  onClose,
  onCreated,
  defaultType = "automated",
  defaultFolderId,
  versionId,
  projectId,
  selectedTestCaseType,
}: CreateCycleDialogProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<CycleType>("automated");
  const [folderId, setFolderId] = useState<number | null>(
    defaultFolderId ?? null,
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [criteria, setCriteria] = useState("");
  const [selectedMobileAppArtifactId, setSelectedMobileAppArtifactId] = useState("");
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [appPathType, setAppPathType] = useState<"UPLOADS" | "UPLOAD_NEW" | "USE_PATH" | "APP_DETAILS">("UPLOADS");
  const [typeMismatchWarningOpen, setTypeMismatchWarningOpen] = useState(false);

  const { userPreferenceData, useWorkspaceVersions } = useAuth();
  const workspaceId =
    userPreferenceData?.data?.workspaceId ??
    userPreferenceData?.data?.workspace?.id ??
    userPreferenceData?.data?.version?.workspaceId ??
    useWorkspaceVersions?.data?.workspace?.id ??
    useWorkspaceVersions?.data?.workspaceId ??
    useWorkspaceVersions?.data?.workspace?.workspaceId ??
    useWorkspaceVersions?.data?.id;
  const rawWorkspaceType = String(
    useWorkspaceVersions?.data?.workspace?.workspaceType ??
      userPreferenceData?.data?.workspaceType ??
      userPreferenceData?.data?.workspace?.workspaceType ??
      userPreferenceData?.data?.workspace?.type ??
      userPreferenceData?.data?.version?.workspaceType ??
      userPreferenceData?.data?.version?.type ??
      useWorkspaceVersions?.data?.workspaceType ??
      "",
  );
  const normalizedWorkspaceType = rawWorkspaceType.replace(/[\s_-]/g, "").toLowerCase();
  const isMobileWorkspace = ["mobile", "mobileweb", "android", "androidnative", "ios", "iosnative"].includes(
    normalizedWorkspaceType,
  );
  const mobilePlatform = normalizedWorkspaceType.includes("android")
    ? "Android"
    : normalizedWorkspaceType.includes("ios")
      ? "iOS"
      : undefined;

  const { data: folders } = useReleaseFolders(versionId ?? null);
  const { data: mobileAppsResponse } = useMobileApps(workspaceId, {
    enabled: !!workspaceId && isMobileWorkspace,
  });
  const mobileApps = useMemo(() => {
    const payload = (mobileAppsResponse as any) ?? {};
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  }, [mobileAppsResponse]);
  const { mutate: createCycle, isPending } = useCreateCycle();
  const { mutate: createConfiguration } = useCreateConfiguration();
  const { showToast } = useToast();

  const mobileAppArtifactOptions = useMemo(() => {
    if (!isMobileWorkspace || !Array.isArray(mobileApps)) return [] as Array<{ value: string; label: string }>;

    const items: Array<{ value: string; label: string }> = [];
    for (const app of mobileApps as Array<any>) {
      const appName = String(app?.name ?? "Mobile App");
      const versions = Array.isArray(app?.versions) ? app.versions : [];
      for (const version of versions) {
        const artifacts = Array.isArray(version?.artifacts) && version.artifacts.length > 0
          ? version.artifacts
          : version?.artifact
            ? [version.artifact]
            : [];
        for (const artifact of artifacts) {
          const artifactId = artifact?.id;
          if (artifactId == null) continue;
          const type = String(artifact?.artifactType ?? artifact?.type ?? "").toUpperCase();
          const versionName = String(version?.version ?? version?.name ?? "Latest");
          const fileName = String(artifact?.fileName ?? artifact?.name ?? "");
          if (mobilePlatform === "Android" && type && !["APK", "AAB"].includes(type)) continue;
          if (mobilePlatform === "iOS" && type && !["IPA", "APP", "ZIP"].includes(type)) continue;
          items.push({
            value: String(artifactId),
            label: `${appName} • ${versionName}${fileName ? ` • ${fileName}` : ""}`,
          });
        }
      }
    }
    return items.sort((left, right) => left.label.localeCompare(right.label));
  }, [isMobileWorkspace, mobileApps, mobilePlatform]);

  const mobileAppOptions = useMemo(
    () => (Array.isArray(mobileApps) ? mobileApps.map((app: any) => ({
      id: String(app?.id ?? ""),
      name: String(app?.name ?? "App"),
      versions: Array.isArray(app?.versions) ? app.versions : [],
    })).filter((app) => app.id) : []),
    [mobileApps],
  );

  const selectedApp = useMemo(
    () => mobileAppOptions.find((app) => app.id === selectedAppId) ?? null,
    [mobileAppOptions, selectedAppId],
  );
  const selectedVersionOptions = useMemo(
    () => (selectedApp?.versions ?? []).map((version: any) => ({
      id: String(version?.id ?? ""),
      label: version?.version ? `v${version.version}` : `Version ${version?.id ?? ""}`,
      artifacts: Array.isArray(version?.artifacts) && version.artifacts.length > 0
        ? version.artifacts
        : version?.artifact
          ? [version.artifact]
          : [],
    })).filter((version) => version.id),
    [selectedApp],
  );
  const selectedVersion = useMemo(
    () => selectedVersionOptions.find((version) => version.id === selectedVersionId) ?? null,
    [selectedVersionOptions, selectedVersionId],
  );
  const selectedArtifactOptions = useMemo(
    () => (selectedVersion?.artifacts ?? []).filter((artifact: any) => {
      const type = String(artifact?.artifactType ?? artifact?.type ?? "").toUpperCase();
      if (mobilePlatform === "Android" && type && !["APK", "AAB"].includes(type)) return false;
      if (mobilePlatform === "iOS" && type && !["IPA", "APP", "ZIP"].includes(type)) return false;
      return artifact?.id != null;
    }).map((artifact: any) => ({
      id: String(artifact?.id ?? ""),
      label: String(artifact?.fileName ?? artifact?.name ?? `Build ${artifact?.id ?? ""}`),
    })).filter((artifact) => artifact.id),
    [mobilePlatform, selectedVersion],
  );

  // Sync folder selection and reset form whenever the dialog opens
  useEffect(() => {
    if (open) {
      setFolderId(defaultFolderId ?? null);
      setName("");
      setType(defaultType);
      setStartDate("");
      setEndDate("");
      setCriteria("");
      setSelectedMobileAppArtifactId("");
      setSelectedAppId(null);
      setSelectedVersionId(null);
      setAppPathType("UPLOADS");
    }
  }, [open, defaultFolderId, defaultType]);

  useEffect(() => {
    if (!isMobileWorkspace || !selectedAppId || !selectedVersionId) {
      if (isMobileWorkspace && !selectedMobileAppArtifactId && mobileAppArtifactOptions[0]?.value) {
        setSelectedMobileAppArtifactId(mobileAppArtifactOptions[0].value);
      }
      return;
    }

    if (selectedArtifactOptions.length === 0) {
      setSelectedMobileAppArtifactId("");
      return;
    }

    const chosen = selectedArtifactOptions.find((artifact) => artifact.id === selectedMobileAppArtifactId)
      ?? selectedArtifactOptions[0];
    setSelectedMobileAppArtifactId(chosen.id);
  }, [isMobileWorkspace, mobileAppArtifactOptions, selectedAppId, selectedArtifactOptions, selectedMobileAppArtifactId, selectedVersionId]);

  useEffect(() => {
    if (!isMobileWorkspace || !mobileAppOptions.length) return;
    if (!selectedAppId) {
      setSelectedAppId(mobileAppOptions[0].id);
    }
  }, [isMobileWorkspace, mobileAppOptions, selectedAppId]);

  useEffect(() => {
    if (!isMobileWorkspace || !selectedAppId || !mobileAppOptions.length) return;
    const app = mobileAppOptions.find((entry) => entry.id === selectedAppId);
    if (!app?.versions?.length) {
      setSelectedVersionId(null);
      return;
    }
    if (!selectedVersionId || !app.versions.some((version: any) => String(version?.id ?? "") === selectedVersionId)) {
      const firstVersion = app.versions[0];
      setSelectedVersionId(String(firstVersion?.id ?? ""));
    }
  }, [isMobileWorkspace, mobileAppOptions, selectedAppId, selectedVersionId]);

  // Validation: end date must not precede start date (both optional).
  const dateError =
    startDate && endDate && endDate < startDate
      ? "End date can't be before the start date"
      : null;
  const canSubmit =
    !!name.trim() && versionId != null && !isPending && !dateError;

  const createCycleDraft = () => {
    if (versionId == null) {
      showToast({ title: "No workspace version selected", variant: "error" });
      return;
    }
    if (dateError) {
      showToast({ title: dateError, variant: "error" });
      return;
    }
    createCycle(
      {
        name: name.trim(),
        type,
        workspace_version_id: versionId,
        project_id: projectId ?? null,
        release_folder_id: folderId,
        start_date: startDate || null,
        end_date: endDate || null,
        completion_criteria: criteria.trim() || null,
      },
      {
        onSuccess: (data) => {
          const mobileSelection = isMobileWorkspace && selectedMobileAppArtifactId ? Number(selectedMobileAppArtifactId) : null;
          if (mobileSelection != null) {
            createConfiguration(
              {
                name: `${name.trim()} - default config`,
                config_type: "AUTOMATION",
                lab: "BrowserStack",
                mobile_app_artifact_id: mobileSelection,
                screenshot_capture: 2,
                page_timeout: 30,
                element_timeout: 30,
                retry_count: 0,
                workspace_version_id: versionId,
              },
              {
                onError: () => {
                  showToast({ title: "Cycle created, but app selection was not saved", variant: "warning" });
                },
              },
            );
          }
          showToast({ title: "Cycle created", variant: "success" });
          onCreated?.(data);
          resetAndClose();
        },
        onError: () => {
          showToast({ title: "Failed to create cycle", variant: "error" });
        },
      },
    );
  };

  const handleCreate = () => {
    const cycleType = type === "automated" ? "AUTOMATION" : "MANUAL";
    if (selectedTestCaseType && selectedTestCaseType !== cycleType) {
      setTypeMismatchWarningOpen(true);
      return;
    }
    createCycleDraft();
  };

  const resetAndClose = () => {
    onClose();
  };

  return (
    <>
      <DialogBox
        open={open}
        onOpenChange={(v) => {
          if (!v) resetAndClose();
        }}
        title={
          <div className="flex items-center gap-3">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-muted shrink-0">
              <Plus className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="text-base font-medium">Create New Cycle</span>
          </div>
        }
        size="md"
        overlayClassName="z-[110]"
        className="z-[120]"
        footer={
          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!canSubmit}
              data-tour="cycle-create-submit"
            >
              {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create as Draft
            </Button>
          </div>
        }
      >
        <div className="w-full space-y-5 px-6 py-5">
          {/* Type — tab-style selector like Create Test Case */}
          <div data-tour="cycle-type-selector">
            <TextLabel>Type</TextLabel>
            <div className="mt-1.5">
              <div className="inline-flex p-0.5 rounded-lg bg-secondary border border-border">
                {(
                  [
                    { id: "automated", label: "Automated", icon: Cpu },
                    { id: "manual", label: "Manual", icon: Hand },
                  ] as const
                ).map((opt) => {
                  const active = type === opt.id;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setType(opt.id)}
                      className={`h-7 px-3 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${
                        active
                          ? "bg-background shadow-sm text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon className="size-3" /> {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Name */}
          <div data-tour="cycle-name-input">
            <TextLabel require>Name</TextLabel>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) handleCreate();
              }}
              placeholder="e.g., SIT Cycle 1"
              autoFocus
              className="mt-1.5"
            />
          </div>

          {/* Release folder */}
          <div data-tour="cycle-release-picker">
            <TextLabel>Release folder</TextLabel>
            <Select
              value={folderId != null ? String(folderId) : "none"}
              onValueChange={(v) =>
                setFolderId(v === "none" ? null : Number(v))
              }
            >
              <SelectTrigger className="mt-1.5 w-full">
                <SelectValue placeholder="No folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No folder</SelectItem>
                {(folders ?? []).map((f: any) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <TextLabel>Start date</TextLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <TextLabel>End date</TextLabel>
              <Input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>
          {dateError && (
            <p className="-mt-3 text-[11px] font-medium text-destructive">
              {dateError}
            </p>
          )}

          {/* Completion criteria */}
          <div>
            <TextLabel>Completion criteria (optional)</TextLabel>
            <Input
              type="text"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              placeholder="e.g., 95% pass rate, 0 P0 defects open"
              className="mt-1.5"
            />
          </div>
        </div>
      </DialogBox>
      <AlertDialog
        open={typeMismatchWarningOpen}
        onOpenChange={setTypeMismatchWarningOpen}
        icon={<AlertTriangle />}
        title={`Selected test cases are ${selectedTestCaseType === "MANUAL" ? "manual" : "automated"}`}
        description={`You are creating ${type === "automated" ? "automated" : "manual"} cycle. The selected ${selectedTestCaseType === "MANUAL" ? "manual" : "automated"} test cases will not be added to this cycle. Do you want to continue?`}
        confirmText="Continue"
        cancelText="Cancel"
        confirmColor="default"
        contentClassName="z-[140]"
        onConfirm={() => {
          setTypeMismatchWarningOpen(false);
          createCycleDraft();
        }}
      />
    </>
  );
}
